// Outlet colour coding — SUPER_ADMIN / MANAGEMENT / SALES_COORDINATOR set
// colour rules (e.g. "No order in 90 days" = red). The field app shows every
// outlet in the colour of the first active rule it matches (top to bottom),
// plus a colour guide that filters the list. Admin also turns the feature on
// or off and picks which roles see it. Backend: /api/outlet-colors/*
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Palette, Plus, Sparkles, RefreshCw, ArrowUp, ArrowDown, Pencil, Trash2, Save,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PageHeader from "@/components/PageHeader";

const NAME_MAX = 40;
const DAYS_MAX = 730;
const HEX_RE = /^#[0-9A-F]{6}$/i;

const ROLE_OPTIONS = [
  ["SE", "Sales Executive"], ["ASM", "ASM"], ["ZSM", "ZSM"], ["RSM", "RSM"],
  ["SALES_HEAD", "Sales Head"], ["DISTRIBUTOR", "Distributor"], ["SUPER_ADMIN", "Super Admin"],
  ["MANAGEMENT", "Management"], ["SALES_COORDINATOR", "Sales Co-ordinator"], ["MIS", "MIS"],
];

// Must match the backend's condition types: key, the label the admin picks,
// and which numbers (days / kg) the condition needs.
const CONDITIONS = [
  { type: "no_order_days",         label: "No order in last N days (includes never ordered)",    days: true },
  { type: "ordered_within_days",   label: "Ordered in last N days",                              days: true },
  { type: "never_ordered",         label: "Never ordered" },
  { type: "not_visited_days",      label: "Not visited in last N days (includes never visited)", days: true },
  { type: "visited_no_order_days", label: "Visited but no order in last N days",                 days: true },
  { type: "kg_below",              label: "Bought less than X kg in last N days",                days: true, kg: true },
  { type: "kg_at_least",           label: "Bought X kg or more in last N days",                  days: true, kg: true },
  { type: "new_outlet_days",       label: "New outlet (added in last N days)",                   days: true },
];
const CONDITION_BY_TYPE = Object.fromEntries(CONDITIONS.map((c) => [c.type, c]));

const PALETTE = [
  ["Red", "#DC2626"], ["Orange", "#EA580C"], ["Amber", "#D97706"], ["Yellow", "#CA8A04"],
  ["Green", "#16A34A"], ["Teal", "#0D9488"], ["Blue", "#2563EB"], ["Purple", "#7C3AED"],
  ["Pink", "#DB2777"], ["Grey", "#6B7280"],
];

// What "Add suggested rules" creates (the backend adds these; shown in the empty state).
const SUGGESTED_PREVIEW = [
  ["New outlet", "#2563EB"], ["No order in 90 days", "#DC2626"],
  ["No order in 30 days", "#EA580C"], ["Ordered in last 30 days", "#16A34A"],
];

const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 3 }));

// "30 days" / "1 day"; "N days" while the box is still empty.
function daysText(d) {
  const s = String(d ?? "").trim();
  if (!s) return "N days";
  return `${s} day${Number(s) === 1 ? "" : "s"}`;
}

function kgText(k) {
  const s = String(k ?? "").trim();
  return s && Number.isFinite(Number(s)) ? fmtNum(Number(s)) : "X";
}

// The rule in plain words, shown under its name in the list.
function describeCondition(c) {
  const d = daysText(c?.days);
  const k = kgText(c?.kg);
  switch (c?.type) {
    case "no_order_days":         return `No order in last ${d}`;
    case "ordered_within_days":   return `Ordered in last ${d}`;
    case "never_ordered":         return "Never ordered";
    case "not_visited_days":      return `Not visited in last ${d}`;
    case "visited_no_order_days": return `Visited but no order in last ${d}`;
    case "kg_below":              return `Bought less than ${k} kg in last ${d}`;
    case "kg_at_least":           return `Bought ${k} kg or more in last ${d}`;
    case "new_outlet_days":       return `New outlet (added in last ${d})`;
    default:                      return "Unknown condition";
  }
}

// Name filled in for the admin until they type their own.
function suggestName(type, days, kg) {
  const d = daysText(days);
  const k = kgText(kg);
  const name = {
    no_order_days:         `No order in ${d}`,
    ordered_within_days:   `Ordered in last ${d}`,
    never_ordered:         "Never ordered",
    not_visited_days:      `Not visited in ${d}`,
    visited_no_order_days: `Visited, no order in ${d}`,
    kg_below:              `Bought less than ${k} kg in ${d}`,
    kg_at_least:           `Bought ${k} kg or more in ${d}`,
    new_outlet_days:       `New outlet (last ${d})`,
  }[type] || "";
  return name.slice(0, NAME_MAX);
}

function isTooLight(hex) {
  if (!HEX_RE.test(hex || "")) return false;
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 200;
}

const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

// The chip exactly as field users see it.
function ColorChip({ color, name }) {
  const c = HEX_RE.test(color || "") ? color : "#6B7280";
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
          style={{ backgroundColor: `${c}1F`, color: c }}>
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: c }} />
      <span className="truncate">{name}</span>
    </span>
  );
}

export default function OutletColorCoding() {
  const [data, setData] = useState(null);      // last saved {enabled, roles, rules}
  const [loadError, setLoadError] = useState("");
  const [draft, setDraft] = useState(null);    // Settings card edits, sent by its Save button
  const [savingSettings, setSavingSettings] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busyId, setBusyId] = useState("");    // rule row with a request in flight
  const [editing, setEditing] = useState(null);  // null = closed, {} = new rule, rule = edit
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [addingSuggested, setAddingSuggested] = useState(false);
  const previewSeq = useRef(0);

  // Only the newest count request may update the screen (Recount can be tapped
  // while an older one is still running).
  const loadPreview = useCallback(async () => {
    const seq = ++previewSeq.current;
    setPreviewLoading(true);
    try {
      const { data: p } = await api.get("/outlet-colors/preview");
      if (seq === previewSeq.current) setPreview(p);
    } catch (e) {
      if (seq === previewSeq.current) { setPreview(null); toast.error(formatApiError(e)); }
    } finally {
      if (seq === previewSeq.current) setPreviewLoading(false);
    }
  }, []);

  // resetDraft: overwrite unsaved Settings card edits with what is saved.
  const load = useCallback(async (resetDraft = false) => {
    try {
      const { data: d } = await api.get("/outlet-colors/settings");
      setData(d);
      setLoadError("");
      setDraft((prev) => (resetDraft || !prev ? { enabled: !!d.enabled, roles: d.roles || [] } : prev));
    } catch (e) { setLoadError(formatApiError(e)); }
  }, []);

  const reload = useCallback(() => { load(); loadPreview(); }, [load, loadPreview]);
  useEffect(() => { load(true); loadPreview(); }, [load, loadPreview]);

  const rules = useMemo(
    () => [...(data?.rules || [])].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)),
    [data],
  );
  const activeRules = rules.filter((r) => r.is_active !== false);

  const dirty = !!(draft && data && (draft.enabled !== !!data.enabled || !sameSet(draft.roles, data.roles || [])));

  const toggleRole = (code) => setDraft((d) => ({
    ...d, roles: d.roles.includes(code) ? d.roles.filter((x) => x !== code) : [...d.roles, code],
  }));

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data: d } = await api.put("/outlet-colors/settings", { enabled: draft.enabled, roles: draft.roles });
      setData(d);
      setDraft({ enabled: !!d.enabled, roles: d.roles || [] });
      toast.success("Settings saved");
      loadPreview();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingSettings(false); }
  };

  // Row actions update the list straight away, then re-read from the server.
  const runRowAction = async (id, optimistic, request, okMsg) => {
    setBusyId(id);
    if (optimistic) setData((d) => (d ? { ...d, rules: optimistic(d.rules || []) } : d));
    try {
      await request();
      if (okMsg) toast.success(okMsg);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(""); reload(); }
  };

  const toggleActive = (rule, on) => runRowAction(
    rule.id,
    (list) => list.map((r) => (r.id === rule.id ? { ...r, is_active: on } : r)),
    () => api.patch(`/outlet-colors/rules/${rule.id}`, { is_active: on }),
    on ? "Rule turned on" : "Rule turned off",
  );

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rules.length) return;
    const ids = rules.map((r) => r.id);
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    runRowAction(
      rules[idx].id,
      (list) => list.map((r) => ({ ...r, priority: ids.indexOf(r.id) })),
      () => api.post("/outlet-colors/rules/reorder", { ids }),
    );
  };

  const doDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/outlet-colors/rules/${deleting.id}`);
      toast.success("Rule deleted");
      setDeleting(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleteBusy(false); }
  };

  const addSuggested = async () => {
    setAddingSuggested(true);
    try {
      await api.post("/outlet-colors/rules/suggested");
      toast.success("Suggested rules added");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAddingSuggested(false); }
  };

  const countText = (r) => {
    if (r.is_active === false) return "Off";
    if (previewLoading) return "…";
    if (!preview) return "—";
    const n = preview.counts?.[r.id] ?? 0;
    return `${fmtNum(n)} outlet${n === 1 ? "" : "s"}`;
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="outlet-colors-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Outlet colour coding" }]}
        title="Outlet colour coding"
        subtitle="Colour outlets in the field app by their order and visit history."
        favorite={{ key: "admin.outlet-colors", type: "screen", label: "Outlet colour coding", route: "/admin/outlet-colors" }}
        testid="outlet-colors-header"
      />

      {!data || !draft ? (
        loadError ? (
          <Card className="border-neutral-200 rounded-2xl">
            <CardContent className="p-5 space-y-3 text-sm">
              <div className="text-rose-700" data-testid="oc-load-error">{loadError}</div>
              <Button type="button" variant="outline" size="sm" onClick={() => load(true)}>Try again</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="text-sm text-neutral-500">Loading…</div>
        )
      ) : (
        <>
          {/* ---- Settings ---- */}
          <Card className="border-neutral-200 rounded-2xl" data-testid="oc-settings">
            <CardContent className="p-4 sm:p-5 space-y-4">
              <h2 className="text-sm font-bold text-neutral-900">Settings</h2>

              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-neutral-900">Show colours in the field app</div>
                  <div className="text-xs text-neutral-500 mt-0.5">When off, nobody sees outlet colours.</div>
                </div>
                <Switch checked={draft.enabled}
                        onCheckedChange={(v) => setDraft((d) => ({ ...d, enabled: v }))}
                        disabled={savingSettings}
                        data-testid="oc-enabled" />
              </div>

              <div className={draft.enabled ? "" : "opacity-60"}>
                <div className="text-sm font-semibold text-neutral-900">Who sees the colours</div>
                <div className="text-xs text-neutral-500 mt-0.5">Only people in the ticked roles see colours on their outlet lists.</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {ROLE_OPTIONS.map(([code, label]) => {
                    const on = draft.roles.includes(code);
                    return (
                      <label key={code} data-testid={`oc-role-${code}`}
                             className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${
                               on ? "border-[#E5197F] bg-[#E5197F]/5" : "border-neutral-200 hover:bg-neutral-50"
                             }`}>
                        <Checkbox checked={on} onCheckedChange={() => toggleRole(code)} disabled={savingSettings} />
                        <span>{label}</span>
                      </label>
                    );
                  })}
                </div>
                {draft.enabled && draft.roles.length === 0 && (
                  <div className="text-xs text-amber-700 mt-2">No role is ticked, so nobody will see the colours.</div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 flex-wrap">
                {dirty && <span className="text-xs text-amber-700">You have changes that are not saved.</span>}
                <Button type="button" className="tulsi-gradient text-white" onClick={saveSettings}
                        disabled={!dirty || savingSettings} data-testid="oc-save-settings">
                  {savingSettings ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                  Save
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* ---- Colour rules ---- */}
          <Card className="border-neutral-200 rounded-2xl" data-testid="oc-rules">
            <CardContent className="p-4 sm:p-5 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm font-bold text-neutral-900 flex-1">Colour rules</h2>
                {rules.length > 0 && (
                  <>
                    <Button type="button" variant="outline" size="sm" className="h-8" onClick={loadPreview}
                            disabled={previewLoading} data-testid="oc-recount">
                      <RefreshCw className={"h-3.5 w-3.5 mr-1.5 " + (previewLoading ? "animate-spin" : "")} />
                      Recount
                    </Button>
                    <Button type="button" size="sm" className="h-8 tulsi-gradient text-white" onClick={() => setEditing({})}
                            data-testid="oc-add-rule">
                      <Plus className="h-3.5 w-3.5 mr-1.5" /> Add rule
                    </Button>
                  </>
                )}
              </div>
              <p className="text-xs text-neutral-500">An outlet gets the colour of the first rule it matches, top to bottom.</p>

              {rules.length === 0 ? (
                <div className="rounded-xl border border-dashed border-neutral-300 py-8 px-4 text-center space-y-3" data-testid="oc-empty">
                  <Palette className="h-6 w-6 mx-auto text-neutral-400" />
                  <div className="text-sm font-semibold text-neutral-700">No colour rules yet</div>
                  <div className="text-xs text-neutral-500">Start with these suggested rules, or make your own.</div>
                  <div className="flex flex-wrap justify-center gap-1.5">
                    {SUGGESTED_PREVIEW.map(([n, c]) => <ColorChip key={n} name={n} color={c} />)}
                  </div>
                  <div className="flex flex-wrap justify-center gap-2 pt-1">
                    <Button type="button" variant="outline" onClick={addSuggested} disabled={addingSuggested}
                            data-testid="oc-add-suggested">
                      {addingSuggested ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
                      Add suggested rules
                    </Button>
                    <Button type="button" className="tulsi-gradient text-white" onClick={() => setEditing({})}
                            data-testid="oc-add-rule-empty">
                      <Plus className="h-4 w-4 mr-1.5" /> Add rule
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto rounded-xl border border-neutral-200 divide-y divide-neutral-100"
                     data-testid="oc-rule-list">
                  {rules.map((r, i) => (
                    <div key={r.id} data-testid={`oc-rule-${r.id}`}
                         className={"p-3 flex flex-wrap items-center gap-x-3 gap-y-2 " + (r.is_active !== false ? "" : "bg-neutral-50")}>
                      <div className="flex items-start gap-2.5 min-w-0 flex-1 basis-[200px]">
                        <span className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-black/10"
                              style={{ backgroundColor: r.color }} />
                        <div className="min-w-0">
                          <div className={"text-sm font-semibold break-words " + (r.is_active !== false ? "text-neutral-900" : "text-neutral-400")}>
                            {r.name}
                          </div>
                          <div className="text-xs text-neutral-500 break-words">{describeCondition(r.condition)}</div>
                        </div>
                      </div>
                      <div className="text-xs font-semibold text-neutral-700 whitespace-nowrap tabular-nums"
                           title="Outlets that get this colour now" data-testid={`oc-count-${r.id}`}>
                        {countText(r)}
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-1 ml-auto">
                        <label className="flex items-center gap-1.5 text-xs text-neutral-500 mr-1 cursor-pointer">
                          <Switch checked={r.is_active !== false} onCheckedChange={(v) => toggleActive(r, v)}
                                  disabled={!!busyId} data-testid={`oc-active-${r.id}`} />
                          Active
                        </label>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Move up"
                                title="Move up" disabled={!!busyId || i === 0} onClick={() => move(i, -1)}
                                data-testid={`oc-up-${r.id}`}>
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Move down"
                                title="Move down" disabled={!!busyId || i === rules.length - 1} onClick={() => move(i, 1)}
                                data-testid={`oc-down-${r.id}`}>
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="outline" size="sm" className="h-8 px-2" onClick={() => setEditing(r)}
                                disabled={!!busyId} data-testid={`oc-edit-${r.id}`}>
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                        <Button type="button" variant="outline" size="sm"
                                className="h-8 px-2 text-rose-700 border-rose-200 hover:bg-rose-50"
                                onClick={() => setDeleting(r)} disabled={!!busyId} data-testid={`oc-delete-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {rules.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600" data-testid="oc-totals">
                  <span>Total outlets: <b>{previewLoading ? "…" : fmtNum(preview?.total_outlets)}</b></span>
                  <span>With no colour: <b>{previewLoading ? "…" : fmtNum(preview?.no_colour)}</b></span>
                </div>
              )}

              {activeRules.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <div className="text-xs font-semibold text-neutral-700">Colour guide field users will see</div>
                  <div className="flex flex-wrap gap-1.5">
                    {activeRules.map((r) => <ColorChip key={r.id} name={r.name} color={r.color} />)}
                  </div>
                  {!data.enabled && (
                    <div className="text-xs text-amber-700">Colours are switched off, so field users do not see this yet.</div>
                  )}
                </div>
              )}

              <p className="text-[11px] text-neutral-400">
                Counts are for all active outlets and are worked out when you open this page.
              </p>
            </CardContent>
          </Card>
        </>
      )}

      {editing && (
        <RuleDialog
          rule={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}

      <AlertDialog open={!!deleting} onOpenChange={(v) => { if (!v && !deleteBusy) setDeleting(null); }}>
        <AlertDialogContent data-testid="oc-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this rule?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleting?.name}” will be removed. Outlets will no longer get this colour. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBusy}>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doDelete(); }}
              disabled={deleteBusy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="oc-delete-confirm"
            >
              {deleteBusy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Add / edit one rule. `rule` is {} for a new rule.
function RuleDialog({ rule, onClose, onSaved }) {
  const isNew = !rule.id;
  const init = rule.condition || { type: "no_order_days", days: 90 };
  const initDays = init.days != null ? String(init.days) : "30";
  const initKg = init.kg != null ? String(init.kg) : "5";
  const [type, setType] = useState(CONDITION_BY_TYPE[init.type] ? init.type : "no_order_days");
  const [days, setDays] = useState(initDays);
  const [kg, setKg] = useState(initKg);
  const [color, setColor] = useState(HEX_RE.test(rule.color || "") ? rule.color.toUpperCase() : "#DC2626");
  const [name, setName] = useState(rule.name || suggestName(type, days, kg));
  // While false, the name follows the condition. Turns true once the admin types their own.
  const [nameTouched, setNameTouched] = useState(!isNew && rule.name !== suggestName(init.type, initDays, initKg));
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);

  const meta = CONDITION_BY_TYPE[type];
  const autoName = (t, d, k) => { if (!nameTouched) setName(suggestName(t, d, k)); };
  const onType = (t) => { setType(t); autoName(t, days, kg); };
  const onDays = (v) => { setDays(v); autoName(type, v, kg); };
  const onKg = (v) => { setKg(v); autoName(type, days, v); };

  const daysNum = Number(days);
  const kgNum = Number(kg);
  const nm = name.trim();
  const errors = {};
  if (meta.kg && !(String(kg).trim() !== "" && Number.isFinite(kgNum) && Math.round(kgNum * 1000) > 0)) {
    errors.kg = "Kg must be more than 0.";
  }
  if (meta.days && !(String(days).trim() !== "" && Number.isInteger(daysNum) && daysNum >= 1 && daysNum <= DAYS_MAX)) {
    errors.days = `Days must be a whole number from 1 to ${DAYS_MAX}.`;
  }
  if (!HEX_RE.test(color)) errors.color = "Please pick a colour.";
  if (!nm) errors.name = "Please enter a name.";
  else if (nm.length > NAME_MAX) errors.name = `Name can be at most ${NAME_MAX} letters.`;

  const custom = !PALETTE.some(([, hex]) => hex === color);

  const save = async () => {
    setTried(true);
    const firstError = Object.values(errors)[0];
    if (firstError) { toast.error(firstError); return; }
    const condition = { type };
    if (meta.days) condition.days = daysNum;
    if (meta.kg) condition.kg = kgNum;
    const body = { name: nm, color, condition };
    setSaving(true);
    try {
      if (isNew) await api.post("/outlet-colors/rules", { ...body, is_active: true });
      else await api.patch(`/outlet-colors/rules/${rule.id}`, body);
      toast.success(isNew ? "Rule added" : "Rule saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6 gap-4" data-testid="oc-rule-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="pr-6">{isNew ? "Add colour rule" : "Edit colour rule"}</DialogTitle>
          <DialogDescription>Pick which outlets get this colour.</DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <Label className="text-xs">Condition</Label>
          <Select value={type} onValueChange={onType} disabled={saving}>
            <SelectTrigger className="h-auto min-h-9 whitespace-normal text-left [&>span]:line-clamp-2" data-testid="oc-condition">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CONDITIONS.map((c) => (
                <SelectItem key={c.type} value={c.type} className="whitespace-normal">{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {(meta.days || meta.kg) && (
          <div className="flex flex-wrap gap-3">
            {meta.kg && (
              <div className="space-y-1 w-36">
                <Label className="text-xs">Kg</Label>
                <div className="flex items-center gap-1.5">
                  <Input type="number" inputMode="decimal" min="0" step="any" value={kg} disabled={saving}
                         onChange={(e) => onKg(e.target.value)} data-testid="oc-kg" />
                  <span className="text-sm text-neutral-500">kg</span>
                </div>
                {tried && errors.kg && <div className="text-[11px] text-rose-600">{errors.kg}</div>}
              </div>
            )}
            {meta.days && (
              <div className="space-y-1 w-36">
                <Label className="text-xs">Days</Label>
                <div className="flex items-center gap-1.5">
                  <Input type="number" inputMode="numeric" min={1} max={DAYS_MAX} step={1} value={days} disabled={saving}
                         onChange={(e) => onDays(e.target.value)} data-testid="oc-days" />
                  <span className="text-sm text-neutral-500">days</span>
                </div>
                {tried && errors.days
                  ? <div className="text-[11px] text-rose-600">{errors.days}</div>
                  : <div className="text-[10px] text-neutral-400">1 to {DAYS_MAX}</div>}
              </div>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs">Colour</Label>
          <div className="flex flex-wrap items-center gap-2">
            {PALETTE.map(([label, hex]) => (
              <button key={hex} type="button" title={label} aria-label={label} aria-pressed={color === hex}
                      onClick={() => setColor(hex)} disabled={saving} data-testid={`oc-swatch-${label.toLowerCase()}`}
                      className={"h-8 w-8 rounded-full transition-shadow " + (color === hex
                        ? "ring-2 ring-offset-2 ring-neutral-900"
                        : "ring-1 ring-black/10 hover:ring-2 hover:ring-neutral-300")}
                      style={{ backgroundColor: hex }} />
            ))}
            <label title="Pick any other colour"
                   className={"flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs cursor-pointer " +
                     (custom ? "border-neutral-900 text-neutral-900" : "border-neutral-200 text-neutral-500")}>
              <input type="color" value={HEX_RE.test(color) ? color.toLowerCase() : "#dc2626"} disabled={saving}
                     onChange={(e) => setColor(e.target.value.toUpperCase())} aria-label="Other colour"
                     className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0" data-testid="oc-color-custom" />
              Other
            </label>
          </div>
          {isTooLight(color) && (
            <div className="text-[11px] text-amber-700">This colour is very light and may be hard to read.</div>
          )}
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Name shown to users</Label>
          <Input value={name} maxLength={NAME_MAX} disabled={saving}
                 onChange={(e) => { setName(e.target.value); setNameTouched(e.target.value.trim() !== ""); }}
                 placeholder="e.g. No order in 90 days" data-testid="oc-name" />
          <div className="flex justify-between gap-2 text-[10px]">
            <span className={tried && errors.name ? "text-rose-600 text-[11px]" : "text-neutral-400"}>
              {tried && errors.name ? errors.name : (!nameTouched ? "Filled in for you. You can change it." : "")}
            </span>
            <span className="text-neutral-400 shrink-0">{name.length}/{NAME_MAX}</span>
          </div>
        </div>

        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 space-y-1.5" data-testid="oc-chip-preview">
          <div className="text-[11px] font-semibold text-neutral-500">Field users will see</div>
          <ColorChip color={color} name={nm || "Name"} />
          <div className="text-[11px] text-neutral-500">
            On outlets that match: {describeCondition({ type, days, kg })}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" className="tulsi-gradient text-white" onClick={save} disabled={saving}
                  data-testid="oc-rule-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            {isNew ? "Add rule" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
